import { IS_NATIVE_WEBVIEW, NATIVE_WEBVIEW } from "@/constants/native";
import { blobToBase64, isBlob } from "./utils";

export const NATIVE_WEBVIEW_MESSAGE_TYPES = Object.freeze({
    TEXT: "text",
    BLOB: "blob",
    OBJECT: "object",
});

export class NativeWebViewMessageError extends Error {}

/**
 * Class handling message posting to the native app webview.
 */
export class NativeWebViewMessage {

    constructor(label, data) {
        this._rawData = data;
        this._label = label;
        this._sent = false;
        this._meta = null;

        if(this.isDataBlob) {
            this.addToMeta('size', this._rawData.size);
            this.addToMeta('type', this._rawData.type);
        }
    }

    addToMeta(key, value) {
        this._meta = {
            ...this._meta ?? {},
            [key]: value,
        };
    }

    removeFromMeta(key) {
        if (!this._meta)
            return;

        delete this._meta[key];
        const isMetaEmpty = Object.keys(this._meta).length <= 0;

        if (isMetaEmpty) {
            this._meta = null;
        }
    }

    get isDataBlob() {
        return isBlob(this._rawData);
    }

    async _getComputedData() {

        const dataType = await this._getDataType();

        switch(dataType) {
            case NATIVE_WEBVIEW_MESSAGE_TYPES.BLOB:
                return await blobToBase64(this._rawData);
                
            case NATIVE_WEBVIEW_MESSAGE_TYPES.OBJECT:
                return JSON.stringify(this._rawData);
            
            case NATIVE_WEBVIEW_MESSAGE_TYPES.TEXT:
            default:
                return String(this._rawData);
        }
    }

    async _getDataType() {
        if(this.isDataBlob)
            return NATIVE_WEBVIEW_MESSAGE_TYPES.BLOB;

        else if (typeof this._rawData === "object")
            return NATIVE_WEBVIEW_MESSAGE_TYPES.OBJECT;

        return NATIVE_WEBVIEW_MESSAGE_TYPES.TEXT;
    }

    async getPayload() {
        const data = await this._getComputedData();

        const payload = {
            label: this._label,
            type: await this._getDataType(),
            data,
            meta: this._meta,
        }

        if (this._meta === null || this._meta === undefined)
            delete payload.meta;
        
        return payload;
    }

    async send() {
        if (!IS_NATIVE_WEBVIEW)
            throw new NativeWebViewMessageError("Attempting to send native message out of webview context.");

        const payload = await this.getPayload();
        await NATIVE_WEBVIEW?.postMessage(JSON.stringify(payload));
        this._sent = true;
    }
    
    get sent() {
        return this._sent;
    }
}

/**
 * Send a message to the native webview.
 * @param {string} label 
 * @param {unknown} data
 * @param {Record<string, unknown>} meta
 */
export async function sendNativeWebViewMessage(label, data = null, meta = null) {
    if (!IS_NATIVE_WEBVIEW)
        return;

    const newMessage = new NativeWebViewMessage(label, data);

    Object.entries(meta ?? {})
        .forEach(([key, value]) => newMessage.addToMeta(key, value));

    await newMessage.send();
}

/**
 * Sends a blob to the native webview for downloading.
 * @param {Blob} blob 
 * @param {string} filename
 */
export async function sendBlobToDownloadNativeWebViewMessage(blob, filename) {
    return sendNativeWebViewMessage(
        "blob_download",
        blob,
        {
            filename,
        }
    )
}

/**
 * Sends a file to the native webview for downloading.
 * @param {File} file 
 * @param {string} filename
 */
export async function sendFileToDownloadNativeWebViewMessage(file) {
    return sendBlobToDownloadNativeWebViewMessage(
        file,
        file.name,
    );
} 

export async function sendBlobToPrintNativeWebViewMessage(blob) {
    return sendNativeWebViewMessage(
        "blob_print",
        blob,
    )
}

/**
 * 
 * @param {string} text 
 * @returns 
 */
export async function sendTextToPrintNativeWebViewMessage(text) {
    return sendNativeWebViewMessage(
        "text_print",
        text,
    )
}

export async function sendLogoutNativeWebViewMessage() {
    return sendNativeWebViewMessage(
        "event_logout",
    )
}

export async function sendDisconnectedNativeWebViewMessage() {
    return sendNativeWebViewMessage(
        "event_disconnected",
    )
}

/**
 * Send a message to the native webview to open a given url in the device browser.
 * @param {string | URL} url 
 * @returns 
 */
export async function sendOpenUrlInBrowserNativeWebViewMessage(url) {
    const parsedUrl = new URL(url);
    return sendNativeWebViewMessage(
        "open_url_in_browser",
        {
            url: parsedUrl.toString(),
        }
    );
}


// WEBPACK FOOTER //
// ./src/helpers/native.js